import { useCallback, useRef, useState } from 'react'

type State<T> = {
  loading: boolean
  error: Error | null
  data: T | null
}
type FetchProps = {
  url: string
  headers?: Record<string, string>
  body?: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
}

export const useFetchLazy = <T>() => {
  const [state, setState] = useState<State<T>>({
    data: null,
    error: null,
    loading: false,
  })
  const abortRef = useRef<AbortController | null>(null)

  const fetchData = useCallback(
    async ({ url, headers, method = 'GET', body }: FetchProps) => {
      if (abortRef.current) {
        abortRef.current.abort()
      }
      abortRef.current = new AbortController()
      setState((prevState) => ({
        ...prevState,
        loading: true,
        error: null,
      }))

      try {
        const res = await fetch(url, {
          method,
          headers: { Accept: 'application/json', ...(headers || {}) },
          signal: abortRef.current.signal,
          body,
        })

        if (!res.ok) {
          setState((prevState) => ({
            ...prevState,
            loading: false,
            error: new Error(res.statusText),
          }))

          return
        }
        const json = (await res.json()) as T

        setState({ data: json, error: null, loading: false })

        return json
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        setState({
          data: null,
          error: error instanceof Error ? error : new Error(String(error)),
          loading: false,
        })
      }
    },
    []
  )

  return { ...state, fetchData }
}
